import _ from 'lodash';
import { formatReptSource } from 'utils/formatReptSource';
import { SHOULD_REFRESH_REPORT_DEFAULT_SETTING_INIT_VALUE } from '../actions/constants';
import { applySmartCardVdsOverlay } from '../smartCardVdsOverlay';
import { loadSmartCardVdsCache, saveSmartCardVdsCache } from '../smartCardVdsCache';
import * as Types from '../actions/types';

const emptyCohortOptions = { myCohorts: [], sharedCohorts: [] };

const mapCohortItemToOption = (item) => {
  if (!item) {
    return null;
  }

  const value = item.luid;
  const label = item.displayText;

  if (!label || !value) {
    return null;
  }

  return {
    value: String(value),
    label: String(label),
    owner: item.username != null ? String(item.username) : null,
    reptSource: item.reptSource != null ? String(item.reptSource) : null,
  };
};

const toCohortOptions = (cohortPersonal, cohortPublic) => {
  const myCohorts = (Array.isArray(cohortPersonal) ? cohortPersonal : []).map(mapCohortItemToOption).filter(Boolean);
  const sharedCohorts = (Array.isArray(cohortPublic) ? cohortPublic : []).map(mapCohortItemToOption).filter(Boolean);

  return { myCohorts, sharedCohorts };
};

// smartsBase holds the raw /user/fo-detail cards; smarts is smartsBase with the
// current sc-vds overlay applied. Non-VDS field updates must hit both lists.
const mapSmartsAndBase = (state, mapFn) => ({
  smarts: Array.isArray(state.smarts) ? state.smarts.map(mapFn) : state.smarts,
  smartsBase: Array.isArray(state.smartsBase) ? state.smartsBase.map(mapFn) : state.smartsBase
});

const initialState = {
  legacies: [],
  legaciesLoading: false,
  explorers: [],
  explorersLoading: false,
  smarts: null,
  smartsBase: null,
  smartsTenantCd: null,
  foDetailRequestId: 0,
  smartCardVdsByScId: {},
  smartCardVdsLoading: false,
  smartCardVdsFilterHash: null,
  smartCardVdsTenantCd: null,
  smartCardVdsCache: loadSmartCardVdsCache(),
  dismissOverrides: {},
  removedSmartCardIds: [],
  adHocs: [],
  customs: [],
  customsLoading: false,
  oftentimes: [],
  metrics: [],
  menuSearchOptions: [],
  reportMenus: [],
  defaultMetrics: [],
  openReportViewDialog: false,
  reportUrl: null,
  inspection: {},
  reportTags: [],
  currentReportCategory: '',
  currentReportTitle: '',
  adHocPublic: [],
  adHocPersonal: [],
  filteredAdHocPersonal: [],
  filteredAdHocPublic: [],
  displayTexts: [],
  reportsToDelete: [],
  defaultSettingRefresh: SHOULD_REFRESH_REPORT_DEFAULT_SETTING_INIT_VALUE,
  exclusive: [],
  exclusiveLoading: false,
  tableauServiceUrl: null,
  reportBaseUrl: '',
  reportOriginComponent: '',
  availableDates: null,
  oldestAvailableDate: null,
  planStartDates: null,
  planYearDateRanges: [],
  planEndDate: null,
  availableDateBoundsCache: {},
  timeWindowDates: null,
  timeWindowDatesTimeWindow: null,
  globalFilterOptions: {
    division: [],
    plan: []
  },
  isGlobalFilterOptionsLoading: false,
  globalFilterOptionsRequestKey: null,
  globalFilterOptionsLastSuccessKey: null,
  globalFilterOptionsCanceledKey: null,
  cohortPublic: [],
  cohortPersonal: [],
  cohortOptions: emptyCohortOptions,
  cohortOptionsTenantCd: null,
  showHiddenSmartsLink: false,
  pendingHiddenSmartCardIds: []
};

export default function foReducer(state = initialState, action) {
  switch (action.type) {
    case Types.SET_REPORT_FAVORITED_LOCAL: {
      const reportId = action?.payload?.reportId;
      const favorited = action?.payload?.favorited;

      if (reportId === null || reportId === undefined) return state;

      const sameId = (a, b) => String(a) === String(b);
      const updateList = (list) =>
        Array.isArray(list)
          ? list.map((item) => (sameId(item?.id, reportId) ? { ...item, favorited, createdDttm: favorited ? (item.createdDttm ?? Date.now()) : item.createdDttm } : item))
          : list;

      return {
        ...state,
        legacies: updateList(state.legacies),
        explorers: updateList(state.explorers),
        smarts: updateList(state.smarts),
        smartsBase: updateList(state.smartsBase),
        adHocs: updateList(state.adHocs),
        customs: updateList(state.customs),
        oftentimes: updateList(state.oftentimes),
        exclusive: updateList(state.exclusive),
        currentReport: sameId(state.currentReport?.id, reportId)
          ? { ...state.currentReport, favorited, createdDttm: favorited ? (state.currentReport?.createdDttm ?? Date.now()) : state.currentReport?.createdDttm }
          : state.currentReport
      };
    }
    case Types.FETCH_MY_METRIC_SUCCESS:
      const sortedMetrics = _.orderBy(action.payload, [(item) => parseInt(item.order)], ['asc']);
      return {
        ...state,
        metrics: sortedMetrics
      };
    case Types.FETCH_DEFAULT_METRIC_SUCCESS:
      return {
        ...state,
        defaultMetrics: action.payload
      };
    case Types.FETCH_SAVED_REPORT_SUCCESS:
      let initPublic = _.orderBy(action.payload?.adHocPublic, 'displayText', 'asc');
      let initPersonal = _.orderBy(action.payload?.adHocPersonal, 'displayText', 'asc');
      return {
        ...state,
        adHocPublic: initPublic,
        adHocPersonal: initPersonal,
        filteredAdHocPersonal: initPersonal,
        filteredAdHocPublic: initPublic
      };
    case Types.SORT_SUCCESS:
      if (action.payload.isPublic && action.payload.folders && action.payload.folderKey) {
        if (!action.payload.data) {
          let newMap = new Map(action.payload.folders);
          let newData = [];
          if (action.payload.flag) {
            newData = _.orderBy(action.payload.folders.get(action.payload.folderKey), ['displayText'], ['desc']);
          } else {
            newData = _.orderBy(action.payload.folders.get(action.payload.folderKey), ['displayText'], ['asc']);
          }
          newMap.set(action.payload.folderKey, newData);
          return {
            ...state,
            filteredAdHocPublic: Array.from(newMap.values()).flat(1)
          };
        } else {
          let newData = [];
          if (action.payload.flag) {
            newData = _.orderBy(action.payload.data, 'displayText', 'desc');
          } else {
            newData = _.orderBy(action.payload.data, 'displayText', 'asc');
          }
          return { ...state, adHocPublic: newData };
        }
      } else {
        if (!action.payload.isPublic) {
          if (action.payload.flag) {
            const newData = _.orderBy(action.payload.data, 'displayText', 'desc');
            return { ...state, filteredAdHocPersonal: newData };
          } else {
            const newData = _.orderBy(action.payload.data, 'displayText', 'asc');
            return { ...state, filteredAdHocPersonal: newData };
          }
        }
      }
      break;
    case Types.FILTER_SUCCESS:
      if (action.payload.isPublic && action.payload.folders && action.payload.folderKey) {
        if (!action.payload.data) {
          let newMap = new Map(action.payload.folders);
          let newData = _.filter(action.payload.folders.get(action.payload.folderKey), function (o) {
            return o.displayText ? o.displayText.toLowerCase().includes(action.payload.searchValue.toLowerCase()) : false;
          });

          if (Array.isArray(newData) && newData.length > 0) {
            newData = _.orderBy(newData, 'displayText', action.payload.order);
          }
          newMap.set(action.payload.folderKey, newData);
          return {
            ...state,
            filteredAdHocPublic: Array.from(newMap.values()).flat(1)
          };
        } else {
          let newData = _.filter(
            [...action.payload.folders.values()].flatMap((s) => s),
            function (o) {
              return o.displayText ? o.displayText.toLowerCase().includes(action.payload.searchValue.toLowerCase()) : false;
            }
          );
          if (Array.isArray(newData) && newData.length > 0) {
            newData = _.orderBy(newData, 'displayText', action.payload.order);
          }
          return { ...state, adHocPublic: newData };
        }
      }

      if (!action.payload.isPublic && action.payload.data && Array.isArray(action.payload.data)) {
        let newData = _.filter(action.payload.data, function (o) {
          return o.displayText ? o.displayText.toLowerCase().includes(action.payload.searchValue.toLowerCase()) : false;
        });
        newData = _.orderBy(newData, 'displayText', action.payload.order);
        return { ...state, filteredAdHocPersonal: newData };
      }
      break;
    case Types.FETCH_REPORT_CUSTOM_REQUEST:
      return {
        ...state,
        customsLoading: true
      };
    case Types.FETCH_REPORT_CUSTOM_FAILURE:
      return {
        ...state,
        customsLoading: false
      };
    case Types.FETCH_REPORT_CUSTOM_SUCCESS:
      return {
        ...state,
        customs: _.orderBy(action.payload, ['updateDt'], ['desc']),
        customsLoading: false
      };
    case Types.FETCH_FO_DETAIL_SUCCESS:
      // A refresh issued before one already applied carries pre-restore data, and
      // dismissOverrides may already be cleared, so it would re-hide restored cards.
      const foDetailRequestId = action.meta?.requestId;
      if (foDetailRequestId != null && foDetailRequestId < (state.foDetailRequestId || 0)) {
        return state;
      }
      let searchOptions = [];
      let reportMenus = [];
      const removedIds = new Set(state.removedSmartCardIds || []);
      const smarts = Array.isArray(action.payload?.smarts) ? action.payload.smarts.filter((item) => !removedIds.has(String(item.id))) : [];

      if (action.payload?.legacies && Array.isArray(action.payload.legacies)) {
        Array.prototype.push.apply(searchOptions, action.payload.legacies);
        Array.prototype.push.apply(reportMenus, action.payload.legacies);
      }

      if (action.payload?.explorers && Array.isArray(action.payload.explorers)) {
        Array.prototype.push.apply(searchOptions, action.payload.explorers);
        Array.prototype.push.apply(reportMenus, action.payload.explorers);
      }

      if (action.payload?.smarts && Array.isArray(action.payload.smarts)) {
        Array.prototype.push.apply(searchOptions, action.payload.smarts);
      }

      searchOptions = searchOptions
        .map((opt) => {
          return {
            displayText: opt.displayText,
            category: opt.category,
            reptSource: formatReptSource(action.payload.tbUrl, action.payload.tenantCd, opt.reptSource),
            description: opt.description,
            searchTerm: opt.displayText
          };
        })
        .sort(function (a, b) {
          return a.displayText < b.displayText ? -1 : 1;
        });
      const overrides = { ...(state.dismissOverrides || {}) };
      const finalSmarts = smarts.map((item) => {
        const key = String(item?.id);
        if (Object.prototype.hasOwnProperty.call(overrides, key)) {
          const serverDismissed = item.dismissed;
          const overideValue = overrides[key];
          if (serverDismissed === overideValue) {
            delete overrides[key];
            return item;
          }
          return { ...item, dismissed: overideValue };
        }
        return item;
      });

      // Only reuse the sc-vds overlay when it belongs to this tenant; scIds are
      // shared across tenants, so a stale overlay would show the prior tenant's values.
      const scVdsSameTenant = state.smartCardVdsTenantCd === action.payload?.tenantCd;
      const overlayByScId = scVdsSameTenant ? state.smartCardVdsByScId : {};

      return {
        ...state,
        smarts: applySmartCardVdsOverlay(finalSmarts, overlayByScId),
        smartsBase: finalSmarts,
        smartsTenantCd: action.payload?.tenantCd ?? null,
        foDetailRequestId: foDetailRequestId ?? state.foDetailRequestId,
        smartCardVdsByScId: overlayByScId,
        smartCardVdsTenantCd: action.payload?.tenantCd ?? state.smartCardVdsTenantCd,
        dismissOverrides: overrides,
        explorers: action.payload?.explorers,
        adHocs: action.payload?.adHocs,
        metrics: action.payload?.metrics,
        oftentimes: action.payload?.oftentimes,
        menuSearchOptions: searchOptions,
        reportMenus: reportMenus,
        tableauServiceUrl: action.payload?.tbUrl,
        showHiddenSmartsLink: false
      };
    case Types.SET_SMART_CARD_DISMISSED_LOCAL: {
      const { reportId, dismissed } = action.payload || {};
      if (reportId === null || reportId === undefined) return state;
      if (!Array.isArray(state.smarts)) return state;
      const sameId = (a, b) => String(a) === String(b);
      return {
        ...state,
        ...mapSmartsAndBase(state, (item) => (sameId(item?.id, reportId) ? { ...item, dismissed } : item))
      };
    }
    case Types.SET_SMART_CARDS_DISMISSED_LOCAL: {
      const { reportIds, dismissed } = action.payload || {};
      if (!Array.isArray(reportIds) || reportIds.length === 0) return state;
      if (!Array.isArray(state.smarts)) return state;
      const reportIdsSet = new Set(reportIds.map(String));

      const dismissOverrides = { ...(state.dismissOverrides || {}) };
      reportIds.forEach((id) => {
        dismissOverrides[String(id)] = dismissed;
      });
      return {
        ...state,
        ...mapSmartsAndBase(state, (item) => (reportIdsSet.has(String(item?.id)) ? { ...item, dismissed } : item)),
        dismissOverrides
      };
    }
    case Types.SET_SMART_CARD_PENDING: {
      const { reportId, pending } = action.payload || {};
      if (reportId === null || reportId === undefined) return state;
      const set = new Set(state.pendingHiddenSmartCardIds || []);
      if (pending) {
        set.add(String(reportId));
      } else {
        set.delete(String(reportId));
      }
      return {
        ...state,
        pendingHiddenSmartCardIds: Array.from(set)
      };
    }
    case Types.UPDATE_SMART_CARD_VDS_VALUES: {
      const byScId = action.payload?.byScId || {};
      const { cacheKey, timestamp, tenantCd } = action.payload || {};
      const nextCache = cacheKey ? { ...state.smartCardVdsCache, [cacheKey]: { byScId, timestamp: timestamp || Date.now() } } : state.smartCardVdsCache;
      if (cacheKey) saveSmartCardVdsCache(nextCache);
      return {
        ...state,
        smartCardVdsByScId: byScId,
        smartCardVdsLoading: false,
        smartCardVdsFilterHash: action.payload?.filterHash ?? state.smartCardVdsFilterHash,
        smartCardVdsTenantCd: tenantCd ?? state.smartCardVdsTenantCd,
        smartCardVdsCache: nextCache,
        // Overlay the raw fo-detail cards, never the previously overlaid ones,
        // so cards missing from this response fall back to their fo-detail values.
        smarts: applySmartCardVdsOverlay(state.smartsBase, byScId)
      };
    }
    // Result of a request that is no longer the active tenant + filter context:
    // it may only warm the cache, never the overlay/loading/hash state.
    case Types.CACHE_SMART_CARD_VDS_VALUES: {
      const { cacheKey, byScId, timestamp } = action.payload || {};
      if (!cacheKey) return state;
      const nextCache = {
        ...state.smartCardVdsCache,
        [cacheKey]: { byScId: byScId || {}, timestamp: timestamp || Date.now() }
      };
      saveSmartCardVdsCache(nextCache);
      return {
        ...state,
        smartCardVdsCache: nextCache
      };
    }
    case Types.SET_SMART_CARD_VDS_LOADING: {
      const { loading, filterHash } = action.payload || {};
      return {
        ...state,
        smartCardVdsLoading: Boolean(loading),
        ...(filterHash !== undefined ? { smartCardVdsFilterHash: filterHash } : {})
      };
    }
    case Types.FETCH_REPORT_STANDARD_REQUEST:
      return {
        ...state,
        legaciesLoading: true
      };
    case Types.FETCH_REPORT_STANDARD_FAILURE:
      return {
        ...state,
        legaciesLoading: false
      };
    case Types.FETCH_REPORT_STANDARD_SUCCESS:
      return {
        ...state,
        legaciesLoading: false,
        legacies: action.payload
      };
    case Types.REMOVE_HIDDEN_SMART_CARD: {
      const idsToRemove = new Set(Array.isArray(action.payload) ? action.payload.map(String) : []);
      const smarts = Array.isArray(state.smarts) ? state.smarts : [];
      const smartsBase = Array.isArray(state.smartsBase) ? state.smartsBase : [];
      return {
        ...state,
        smarts: smarts.filter((item) => !idsToRemove.has(String(item.id))),
        smartsBase: smartsBase.filter((item) => !idsToRemove.has(String(item.id))),
        removedSmartCardIds: [...new Set([...state.removedSmartCardIds, ...idsToRemove])],
        showHiddenSmartsLink: false
      };
    }
    case Types.RESTORE_SMART_CARD: {
      const idsToRestore = new Set(Array.isArray(action.payload) ? action.payload.map(String) : []);
      const removedSmartCardIds = (state.removedSmartCardIds || []).filter((id) => !idsToRestore.has(String(id)));

      return {
        ...state,
        removedSmartCardIds
      };
    }
    case Types.CLEAR_REMOVE_HIDDEN_SMART_CARD: {
      return {
        ...state,
        removedSmartCardIds: []
      };
    }
    case Types.OPEN_REPORT_VIEWER_REQUEST:
      return {
        ...state,
        openReportViewDialog: true,
        reportUrl: action.reportUrl,
        currentReportCategory: action.category,
        currentReportTitle: action.title,
        reportBaseUrl: action.reportBaseUrl,
        currentReport: { ...action.report, id: +action.report.id || action.report.id, reportUrl: action.reportUrl },
        reportOriginComponent: action.reportOriginComponent || ''
      };
    case Types.CLOSE_REPORT_VIEWER_REQUEST:
      return {
        ...state,
        openReportViewDialog: false
      };
    case Types.FETCH_INSPECTION_SUCCESS:
      return {
        ...state,
        inspection: action.payload
      };
    case Types.FETCH_REPORT_EXPLORER_REQUEST:
      return {
        ...state,
        explorersLoading: true
      };
    case Types.FETCH_REPORT_EXPLORER_FAILURE:
      return {
        ...state,
        explorersLoading: false
      };
    case Types.FETCH_REPORT_EXPLORER_SUCCESS:
      return {
        ...state,
        explorers: action.payload,
        explorersLoading: false
      };
    case Types.FETCH_REPORT_TAGS:
      return {
        ...state,
        reportTags: action.payload
      };
    case Types.FETCH_ALL_DISPLAY_TEXT_SUCCESS:
      return {
        ...state,
        displayTexts: action.payload
      };
    case Types.FETCH_REPORT_TO_DELETE_SUCCESS:
      return {
        ...state,
        reportsToDelete: action.payload
      };
    case Types.REPORT_DEFAULT_SETTING_REFRESH:
      return {
        ...state,
        defaultSettingRefresh: state.defaultSettingRefresh + 1
      };
    case Types.FETCH_REPORT_EXCLUSIVE_REQUEST:
      return {
        ...state,
        exclusiveLoading: true
      };
    case Types.FETCH_REPORT_EXCLUSIVE_FAILURE:
      return {
        ...state,
        exclusiveLoading: false
      };
    case Types.FETCH_REPORT_EXCLUSIVE_SUCCESS:
      return {
        ...state,
        exclusive: Array.isArray(action.payload) ? action.payload : [],
        exclusiveLoading: false
      };
    case Types.OPEN_GLOBAL_CONFIG_REQUEST:
      return {
        ...state,
        gconfig: action.payload
      };
    case Types.RESET_REPORT_ORIGIN_COMPONENT:
      return {
        ...state,
        reportOriginComponent: ''
      };
    case Types.FETCH_AVAILABLE_DATE_BOUNDS_SUCCESS:
      return {
        ...state,
        availableDates: action.payload?.latestDate,
        oldestAvailableDate: action.payload?.oldestDate,
        planStartDates: action.payload?.planStartDate,
        planYearDateRanges: action.payload?.planYearDateRanges ?? [],
        planEndDate: action.payload?.planEndDate,
        availableDateBoundsCache: action.meta?.cacheKey
          ? {
              ...state.availableDateBoundsCache,
              [action.meta.cacheKey]: {
                latestDate: action.payload?.latestDate,
                oldestDate: action.payload?.oldestDate,
                planStartDate: action.payload?.planStartDate,
                planYearDateRanges: action.payload?.planYearDateRanges ?? [],
                planEndDate: action.payload?.planEndDate
              }
            }
          : state.availableDateBoundsCache
      };
    case Types.CLEAR_AVAILABLE_DATE_BOUNDS_CACHE:
      return {
        ...state,
        availableDateBoundsCache: {}
      };
    case Types.SET_TIME_WINDOW_DATES:
      return {
        ...state,
        timeWindowDates: action.payload,
        timeWindowDatesTimeWindow: action.meta?.timeWindow || null
      };
    case Types.FETCH_GLOBAL_FILTER_OPTIONS_REQUEST:
      return {
        ...state,
        isGlobalFilterOptionsLoading: true,
        globalFilterOptionsRequestKey: action?.meta?.requestKey ?? null
      };
    case Types.FETCH_GLOBAL_FILTER_OPTIONS_SUCCESS: {
      const requestKey = action?.meta?.requestKey ?? null;
      if (state.globalFilterOptionsRequestKey && !requestKey) {
        // A keyed request is in-flight but this response can't be correlated.
        return state;
      }
      if (requestKey && state.globalFilterOptionsRequestKey && requestKey !== state.globalFilterOptionsRequestKey) {
        // Stale response: ignore it so we don't hide the loading state for a newer in-flight request.
        return state;
      }
      if (requestKey && !state.globalFilterOptionsRequestKey && state.globalFilterOptionsLastSuccessKey && requestKey !== state.globalFilterOptionsLastSuccessKey) {
        // No keyed request is in-flight and this response's key differs from the
        // last applied result.
        return state;
      }

      return {
        ...state,
        isGlobalFilterOptionsLoading: false,
        globalFilterOptionsRequestKey: null,
        globalFilterOptionsLastSuccessKey: requestKey,
        globalFilterOptions: action.payload || { division: [], plan: [] }
      };
    }
    case Types.FETCH_GLOBAL_FILTER_OPTIONS_FAILURE: {
      const requestKey = action?.meta?.requestKey ?? null;
      if (state.globalFilterOptionsRequestKey && !requestKey) {
        // A keyed request is in-flight but this response can't be correlated.
        return state;
      }
      if (requestKey && state.globalFilterOptionsRequestKey && requestKey !== state.globalFilterOptionsRequestKey) {
        return state;
      }
      if (requestKey && !state.globalFilterOptionsRequestKey && state.globalFilterOptionsLastSuccessKey && requestKey !== state.globalFilterOptionsLastSuccessKey) {
        // Superseded out-of-order failure: nothing is in-flight and it does not
        // correspond to the last applied result, so leave state untouched.
        return state;
      }

      return {
        ...state,
        isGlobalFilterOptionsLoading: false,
        globalFilterOptionsRequestKey: null
      };
    }
    case Types.FETCH_GLOBAL_FILTER_OPTIONS_CANCELED: {
      const requestKey = action?.meta?.requestKey ?? null;
      if (requestKey && state.globalFilterOptionsRequestKey && requestKey !== state.globalFilterOptionsRequestKey) {
        return state;
      }

      return {
        ...state,
        isGlobalFilterOptionsLoading: false,
        globalFilterOptionsRequestKey: null,
        globalFilterOptionsCanceledKey: requestKey
      };
    }
    case Types.RESET_GLOBAL_FILTER_OPTIONS:
      return {
        ...state,
        globalFilterOptions: { division: [], plan: [] },
        isGlobalFilterOptionsLoading: false,
        globalFilterOptionsRequestKey: null,
        globalFilterOptionsLastSuccessKey: null,
        globalFilterOptionsCanceledKey: null
      };
    case Types.FETCH_COHORT_OPTIONS_SUCCESS:
      const cohortPublic = Array.isArray(action?.payload?.cohortPublic) ? action.payload.cohortPublic : [];
      const cohortPersonal = Array.isArray(action?.payload?.cohortPersonal) ? action.payload.cohortPersonal : [];

      return {
        ...state,
        cohortPublic,
        cohortPersonal,
        cohortOptions: toCohortOptions(cohortPersonal, cohortPublic),
        cohortOptionsTenantCd: action?.payload?.tenantCd ?? null
      };
    case Types.HIDE_SMART_CARD_REQUEST:
      return {
        ...state,
        showHiddenSmartsLink: true
      };
    case Types.HIDE_SMART_CARD_FAILURE:
      return {
        ...state,
        showHiddenSmartsLink: false
      };
    default:
      return state;
  }
}
